/**
 * AcceptInvitePage — BOLT-022: Public invitation activation page.
 *
 * Matches the Login / ForgotPassword first-touch layout (no AppShell).
 * React Hook Form + Zod for password + confirm password validation.
 * On success: redirect to /login?activated=1.
 * On invalid/expired token: error state with "Back to login" link.
 */
import { useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Divider,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiClient, ApiError } from '@/lib/api-client.js';
import { filledInputSx, outerBoxSx, BrandMark } from './auth-shared.js';

// Client-side schema with confirm password (server schema doesn't need confirm)
const acceptInviteFormSchema = z
  .object({
    display_name: z
      .string()
      .trim()
      .max(100, 'Display name must be 100 characters or less')
      .optional()
      .or(z.literal('')),
    password: z
      .string()
      .min(12, 'Password must be at least 12 characters')
      .max(128, 'Password must be 128 characters or less'),
    confirm_password: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

type AcceptInviteFormValues = z.infer<typeof acceptInviteFormSchema>;

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [tokenError, setTokenError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInviteFormValues>({
    resolver: zodResolver(acceptInviteFormSchema),
    defaultValues: {
      display_name: '',
      password: '',
      confirm_password: '',
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    setSubmitting(true);
    setApiError(null);
    try {
      await apiClient.post('/v1/auth/accept-invite', {
        body: {
          token,
          password: data.password,
          display_name: data.display_name || undefined,
        },
      });
      navigate('/login?activated=1');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_token') {
        setTokenError(true);
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  });

  // Token error state — same outer shell, alert + back link
  if (tokenError || !token) {
    return (
      <Box sx={outerBoxSx}>
        <BrandMark />
        <Box sx={{ width: '100%', maxWidth: 440 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {!token
              ? 'No invitation link was found. Please use the link from your invitation email.'
              : 'This invitation link is invalid or has expired. You can request a new link below, or ask your administrator to send a new invitation.'}
          </Alert>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            {/* BOLT-359 self-service recovery: forgot-password re-issues a
                fresh invitation for a still-invited (non-super_admin) user. */}
            {token && (
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Request a new link
              </Link>
            )}
            <Link component={RouterLink} to="/login" variant="body2">
              Back to log in
            </Link>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={outerBoxSx}>
      <BrandMark />

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
        Activate Your Account
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 440 }}>
        <form onSubmit={onSubmit}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <Stack spacing={1.5}>
            <TextField
              hiddenLabel
              placeholder="Display name (optional)"
              variant="filled"
              {...register('display_name')}
              error={Boolean(errors.display_name)}
              helperText={errors.display_name?.message}
              disabled={submitting}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
              }}
              sx={filledInputSx}
            />

            <TextField
              hiddenLabel
              placeholder="Password"
              type="password"
              autoComplete="new-password"
              variant="filled"
              {...register('password')}
              error={Boolean(errors.password)}
              helperText={errors.password?.message ?? '12–128 characters.'}
              disabled={submitting}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
              }}
              sx={filledInputSx}
            />

            <TextField
              hiddenLabel
              placeholder="Confirm password"
              type="password"
              autoComplete="new-password"
              variant="filled"
              {...register('confirm_password')}
              error={Boolean(errors.confirm_password)}
              helperText={errors.confirm_password?.message}
              disabled={submitting}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
              }}
              sx={filledInputSx}
            />
          </Stack>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, borderRadius: 8, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
          >
            Activate account
          </Button>
        </form>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" variant="body2">
            Back to log in
          </Link>
        </Box>
      </Box>
    </Box>
  );
}
